import { type AnalyticsEvent } from '@autone/analytics';
import { useMemo, useState } from 'react';
import { type Control, type FieldValues, type Path } from 'react-hook-form';

import { type GetCoreProductSeasonsRequest } from '../../../../redux/services';
import { useGetCoreProductSeasonsQuery } from '../../../../redux/services/core';
import { TailwindFormField } from '../../../../tailwind';
import { MultiSelectDropdown } from '../../../multi-select-dropdown';
import { useMultiselectApiFilters } from '../../hooks';
import { type GetMultiselectMapper } from '../../types';
import {
  CLIENT_SIDE_SEARCH_DROPDOWN_LIMIT,
  mapIdsToScopeItem,
} from '../../utilities';
import { getRelevantCoreProductFilterKeys } from '../utilities/core-product-filter-keys';

type SeasonFilters = GetCoreProductSeasonsRequest['body']['filters'];
interface Props<T extends FieldValues> {
  disabled?: boolean;
  control: Control<T>;
  name: Path<T>;
  catalogueMap?: GetMultiselectMapper<SeasonFilters['catalogue'], T>;
  productMap?: GetMultiselectMapper<SeasonFilters['product'], T>;
  productGroupsMap?: GetMultiselectMapper<SeasonFilters['product_groups'], T>;
  locationClustersMap?: GetMultiselectMapper<
    SeasonFilters['location_cluster'],
    T
  >;
  analyticsEvent?: AnalyticsEvent;
}

export const CoreProductSeasonsMultiselect = <T extends FieldValues>({
  control,
  name,
  catalogueMap,
  productMap,
  productGroupsMap,
  locationClustersMap,
  analyticsEvent,
  disabled,
}: Props<T>) => {
  const catalogueFilters = useMultiselectApiFilters({
    control,
    map: catalogueMap,
    relevantFilterKeys: ['seasons', 'events'],
  });

  const productFilters = useMultiselectApiFilters({
    control,
    map: productMap,
    relevantFilterKeys: getRelevantCoreProductFilterKeys(['seasons']),
  });

  const productGroupsFilters = useMultiselectApiFilters({
    control,
    map: productGroupsMap,
    relevantFilterKeys: ['groups'],
  });

  const locationClustersFilters = useMultiselectApiFilters({
    control,
    map: locationClustersMap,
    relevantFilterKeys: ['location_clusters'],
  });

  const [searchTerm, setSearchTerm] = useState('');
  const { data, isLoading } = useGetCoreProductSeasonsQuery({
    query: {
      limit: CLIENT_SIDE_SEARCH_DROPDOWN_LIMIT,
    },
    body: {
      filters: {
        product: productFilters,
        catalogue: catalogueFilters,
        product_groups: productGroupsFilters,
        location_cluster: locationClustersFilters,
        size_run: {},
        size: {},
      },
    },
  });

  const options = useMemo(
    () =>
      data?.items.filter((option) =>
        option.description.toLowerCase().includes(searchTerm.toLowerCase()),
      ) ?? [],
    [data, searchTerm],
  );
  const optionsMap = useMemo(
    () => new Map(options.map((option) => [option.id, option])),
    [options],
  );

  return (
    <TailwindFormField
      control={control}
      name={name}
      render={({ field: { value, onChange } }) => (
        <MultiSelectDropdown
          disabled={disabled}
          className="h-fit"
          loading={isLoading}
          options={options}
          getOptionId={(option) => option.id}
          getOptionLabel={(option) => option?.description}
          onSearchTermChange={setSearchTerm}
          // @ts-expect-error
          selected={value?.map((option) => option.id) ?? []}
          onSelectedChange={(ids) =>
            onChange(mapIdsToScopeItem(optionsMap)(ids))
          }
          analyticsEvent={analyticsEvent}
        />
      )}
    />
  );
};
